import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Pets as PetsIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { normalizeImageUrl } from '../services/imageUtils';
import { getDogAttributes } from '../services/dogAttributeApi';
import { dogsApi } from '../services/api';
import CountryCityFilter from './CountryCityFilter';

// ---------------------------------------------------------------------------
// Chip components — colours driven by type, not hardcoded string values
// ---------------------------------------------------------------------------

const ENERGY_PALETTE = [
  { bg: '#D1FAE5', text: '#065F46' },
  { bg: '#DBEAFE', text: '#1E40AF' },
  { bg: '#FEF3C7', text: '#92400E' },
  { bg: '#FEE2E2', text: '#991B1B' },
];

function pickPalette(paletteArray, id) {
  if (id == null) return { bg: '#F3F4F6', text: '#374151' };
  return paletteArray[id % paletteArray.length];
}

function EnergyChip({ energy }) {
  if (!energy) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const label = energy.nameEn || energy.nameEs || energy.nameHe || String(energy.id);
  const colors = pickPalette(ENERGY_PALETTE, energy.id);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: colors.bg,
        color: colors.text,
        fontWeight: 600,
        fontSize: '0.72rem',
        height: 22,
        textTransform: 'capitalize',
        border: 'none',
      }}
    />
  );
}

const SIZE_PALETTE = [
  { bg: '#F3F4F6', text: '#374151' },
  { bg: '#EDE9FE', text: '#5B21B6' },
  { bg: '#E0F2FE', text: '#075985' },
];

function SizeChip({ size }) {
  if (!size) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const label = size.nameEn || size.nameEs || size.nameHe || String(size.id);
  const colors = pickPalette(SIZE_PALETTE, size.id);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: colors.bg,
        color: colors.text,
        fontWeight: 500,
        fontSize: '0.72rem',
        height: 22,
        textTransform: 'capitalize',
        border: 'none',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Hook: load and cache all dog attributes on mount
// ---------------------------------------------------------------------------

function useDogAttributes() {
  const [breeds, setBreeds]   = useState([]);
  const [energies, setEnergies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getDogAttributes().then((all) => {
      if (cancelled || !Array.isArray(all)) return;
      setBreeds(all.filter((a) => a.type === 'BREED'));
      setEnergies(all.filter((a) => a.type === 'ENERGY_LEVEL'));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return { breeds, energies };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Dogs = () => {
  const navigate = useNavigate();
  const api = useApi();

  const { breeds, energies } = useDogAttributes();

  const [dogs, setDogs] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter state (committed values, sent to API)
  const [search, setSearch]                   = useState('');
  const [breedAttributeId, setBreedAttributeId] = useState('');
  const [energyAttributeId, setEnergyAttributeId] = useState('');
  // Location ids from the closed Country / City lists ('' = any).
  const [countryId, setCountryId]             = useState('');
  const [cityId, setCityId]                   = useState('');

  // Debounce timer ref
  const debounceRef = useRef(null);

  const fetchDogs = useCallback(async (params) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.dogs.adminSearch(params);
      setDogs(data?.content ?? []);
      setTotalElements(data?.totalElements ?? 0);
    } catch (err) {
      setError('Failed to load dogs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Debounced fetch on filter/page changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDogs({
        search,
        breedAttributeId: breedAttributeId || undefined,
        energyAttributeId: energyAttributeId || undefined,
        countryId,
        cityId,
        page,
        size: rowsPerPage,
      });
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, breedAttributeId, energyAttributeId, countryId, cityId, page, rowsPerPage, fetchDogs]);

  const handleClearFilters = () => {
    setSearch('');
    setBreedAttributeId('');
    setEnergyAttributeId('');
    setCountryId('');
    setCityId('');
    setPage(0);
  };

  const hasActiveFilters = search || breedAttributeId || energyAttributeId || countryId || cityId;

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(+e.target.value); setPage(0); };

  return (
    <Box>
      {/* Filter toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); setPage(0); }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Breed</InputLabel>
            <Select
              value={breedAttributeId}
              label="Breed"
              onChange={(e) => { setBreedAttributeId(e.target.value); setPage(0); }}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              {breeds.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.nameEn || b.nameHe || `#${b.id}`}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Energy</InputLabel>
            <Select
              value={energyAttributeId}
              label="Energy"
              onChange={(e) => { setEnergyAttributeId(e.target.value); setPage(0); }}
            >
              <MenuItem value=""><em>Any</em></MenuItem>
              {energies.map((e) => (
                <MenuItem key={e.id} value={e.id}>{e.nameEn || e.nameHe || `#${e.id}`}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <CountryCityFilter
            countryId={countryId}
            cityId={cityId}
            onChange={({ countryId: nextCountry, cityId: nextCity }) => {
              setCountryId(nextCountry);
              setCityId(nextCity);
              setPage(0);
            }}
            loadCities={dogsApi.getFilterCities}
            countrySx={{ minWidth: 170, flex: '0 1 200px' }}
            citySx={{ minWidth: 170, flex: '0 1 200px' }}
          />

          {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ borderColor: 'divider', color: 'text.secondary', flexShrink: 0 }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {loading ? 'Loading…' : `${totalElements.toLocaleString()} dog${totalElements !== 1 ? 's' : ''}`}
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Box>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 52 }} />  {/* image */}
              <col style={{ width: '14%' }} /> {/* name */}
              <col style={{ width: '14%' }} /> {/* breed */}
              <col style={{ width: 56 }} />  {/* age */}
              <col style={{ width: 96 }} />  {/* energy */}
              <col style={{ width: 80 }} />  {/* size */}
              <col style={{ width: 64 }} />  {/* weight */}
              <col style={{ width: 72 }} />  {/* gender */}
              <col />                         {/* owner */}
              <col style={{ width: '10%' }} /> {/* country */}
              <col style={{ width: '10%' }} /> {/* city */}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Breed</TableCell>
                <TableCell align="center">Age</TableCell>
                <TableCell>Energy</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Weight</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>City</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && dogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <PetsIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                      <Typography color="text.secondary">
                        {hasActiveFilters ? 'No dogs match the current filters.' : 'No dogs found.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {dogs.map((dog) => (
                <TableRow
                  key={dog.id}
                  hover
                  onClick={() => navigate(`/dogs/${dog.id}`)}
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover td': { bgcolor: 'transparent' },
                  }}
                >
                  {/* Thumbnail */}
                  <TableCell sx={{ py: 0.75 }}>
                    <Avatar
                      src={normalizeImageUrl(dog.image)}
                      alt={dog.name}
                      variant="rounded"
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha('#6D28D9', 0.1),
                        color: '#6D28D9',
                        borderRadius: 1.5,
                        fontSize: 14,
                      }}
                    >
                      <PetsIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  </TableCell>

                  {/* Name */}
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ color: 'primary.main' }}
                    >
                      {dog.name}
                    </Typography>
                  </TableCell>

                  {/* Breed — nested DogAttributeDto */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {dog.breed?.nameEn || dog.breed?.nameHe || (dog.breed ? `#${dog.breed.id}` : <span style={{ color: '#9CA3AF' }}>—</span>)}
                    </Typography>
                  </TableCell>

                  {/* Age */}
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {dog.age != null ? `${dog.age}y` : '—'}
                    </Typography>
                  </TableCell>

                  {/* Energy */}
                  <TableCell>
                    <EnergyChip energy={dog.energy} />
                  </TableCell>

                  {/* Size */}
                  <TableCell>
                    <SizeChip size={dog.size} />
                  </TableCell>

                  {/* Weight */}
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {dog.weight != null ? `${dog.weight} kg` : '—'}
                    </Typography>
                  </TableCell>

                  {/* Gender */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ textTransform: 'capitalize' }}>
                      {dog.gender || '—'}
                    </Typography>
                  </TableCell>

                  {/* Owner — stopPropagation so row click doesn't also fire */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {dog.ownerUserId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                            maxWidth: 140,
                          }}
                          onClick={() => navigate(`/users/${dog.ownerUserId}`)}
                        >
                          {dog.ownerEmail || `User #${dog.ownerUserId}`}
                        </Typography>
                        <Tooltip title="Open user">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/users/${dog.ownerUserId}`)}
                            sx={{ p: 0.25, color: 'text.disabled' }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>

                  {/* Country */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {dog.ownerCountry || '—'}
                    </Typography>
                  </TableCell>

                  {/* City */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {dog.ownerCity || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>
    </Box>
  );
};

export default Dogs;
